import { GetTokenSilentlyOptions, useAuth0 } from '@auth0/auth0-react'
import { useCallback } from 'react'

import { useMockAuth0 } from '../utils/mock-auth0'
import { isMockAuth } from '@/lib/authMode'

/**
 * Custom hook that handles authentication.
 * Automatically switches between mock auth (for local development) and real Auth0 (for production).
 *
 * Usage: Replace all instances of `useAuth0()` with `useAuth()` in your components.
 */
export const useAuth = () => {
  const useMockAuth = isMockAuth

  // Mock auth for local development, real Auth0 otherwise. The env flag is constant per build,
  // so the hook order never changes between renders.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const auth = useMockAuth ? useMockAuth0() : useAuth0()
  const getToken = auth.getAccessTokenSilently as (
    options?: GetTokenSilentlyOptions
  ) => Promise<unknown>

  // Newer @auth0/auth0-spa-js versions type the token as `string | undefined`. Guarantee a
  // string here so every caller can hand it straight to a service constructor.
  const getAccessTokenSilently = useCallback(
    async (options?: GetTokenSilentlyOptions): Promise<string> => {
      const token = await getToken(options)
      if (typeof token !== 'string' || !token) {
        throw new Error('No access token available')
      }
      return token
    },
    [getToken]
  )

  return { ...auth, getAccessTokenSilently }
}

/**
 * Hook for manual token management and idle handling
 */
export const useTokenManager = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth()
  const useMockAuth = isMockAuth

  const refreshToken = useCallback(async () => {
    if (!isAuthenticated && !useMockAuth) {
      throw new Error('User is not authenticated')
    }

    if (useMockAuth) {
      return 'mock-access-token'
    }

    try {
      return await getAccessTokenSilently({
        timeoutInSeconds: 10,
      })
    } catch (error) {
      console.error('Failed to refresh access token:', error)
      throw error
    }
  }, [getAccessTokenSilently, isAuthenticated, useMockAuth])

  const getTokenSilently = useCallback(async () => {
    if (!isAuthenticated && !useMockAuth) {
      throw new Error('User is not authenticated')
    }

    if (useMockAuth) {
      return 'mock-access-token'
    }

    return await getAccessTokenSilently()
  }, [getAccessTokenSilently, isAuthenticated, useMockAuth])

  return {
    refreshToken,
    getTokenSilently,
    isAuthenticated: isAuthenticated || useMockAuth,
  }
}
